import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import {
  getCompleteAdUrl as getTimeNyangCompleteAdUrl,
  getClaimNpayUrl,
  getForfeitRewardUrl,
  getPlayStartUrl,
  getPlayStop2Url,
  getStartAdUrl as getTimeNyangStartAdUrl,
  getStateUrl,
} from '../../api/generated/time-nyang/game-time-nyang/game-time-nyang.ts';
import STAR_TRAIL_IMAGE from '../../assets/images/time-nyang/star-trail.png';
import GLOW_ELLIPSE_TOP_IMAGE from '../../assets/images/time-nyang/glow-ellipse-top.svg';
import CLOCK_IMAGE from '../../assets/images/time-nyang/clock.gif';
import BURNING_BG_IMAGE from '../../assets/images/time-nyang/burning-bg.png';
import { AuthContext } from '../../context/AuthContext';
import { runAdmob, useAdsListener } from '../../services/admob';
import { usePlatform } from '../../utils/hooks';
import PunchInfoBottomSheet from '../punch/PunchInfoBottomSheet';
import { TIME_NYANG_GUIDE_CONTENT } from './constants';
import './animations.css';
import useTimeNyangGame from './hooks/useTimeNyangGame';
import useTimeNyangRewards from './hooks/useTimeNyangRewards';
import TimeNyangBottomToast from './TimeNyangBottomToast';
import TimeNyangBottomPoint from './components/TimeNyangBottomPoint';
import TimeNyangGamePanel from './components/TimeNyangGamePanel';
import TimeNyangHeader, { TimeNyangTopBar } from './components/TimeNyangHeader';
import TimeNyangPlayLogCard from './components/TimeNyangPlayLogCard';
import TimeNyangRewardPopup from './components/TimeNyangRewardPopup';
import TimeNyangScoreCard from './components/TimeNyangScoreCard';
import { formatGameCryptoError, sha256Hex } from '../../utils/gameCrypto';
import { toClientElapsedSec, toDisplayMs } from './utils/time';

const IS_DEV_BUILD = process.env.NODE_ENV === 'development';
const DEFAULT_TIME_NYANG_MOCK_MODE = IS_DEV_BUILD ? process.env.REACT_APP_TIME_NYANG_MOCK_MODE !== 'false' : false;
const SHOW_TIME_NYANG_MODE_DEBUG_UI = IS_DEV_BUILD && process.env.REACT_APP_SHOW_TIME_NYANG_MODE_DEBUG === 'true';
const TIME_NYANG_API_MODE_STORAGE_KEY = 'time-nyang-api-mode-v1';

const MOCK_AD_LABEL_BY_KIND = {
  'free-play': '무료탐냥 광고 시청 중...',
  'booster-start': '적립부스터 광고 시청 중...',
  'success-reward': '성공 보상 광고 시청 중...',
  'near-point-reward': '위로 보상 광고 시청 중...',
  'ad-rest': '광고 휴식 시청 중...',
  'npay-exchange': 'N Pay 교환 광고 시청 중...',
};

const TIME_NYANG_AD_PLACEMENT_BY_KIND = {
  'free-play': 'freePlay',
  'booster-start': 'boosterEnter',
  'success-reward': 'successReward',
  'near-point-reward': 'comfortReward',
  'ad-rest': 'tenPlayBonus',
  'npay-exchange': 'npayExchange',
};

const padTimePart = (value, length = 2) => String(value).padStart(length, '0');

const formatTimeNyangStoppedAt = (date = new Date()) =>
  `${date.getFullYear()}-${padTimePart(date.getMonth() + 1)}-${padTimePart(date.getDate())} ${padTimePart(
    date.getHours(),
  )}:${padTimePart(date.getMinutes())}:${padTimePart(date.getSeconds())}.${padTimePart(date.getMilliseconds(), 3)}`;

const createTimeNyangStopPayload = async ({ roundToken, elapsedMs }) => {
  const displayMs = toDisplayMs(elapsedMs);
  const clientElapsedSec = toClientElapsedSec(displayMs);
  const stoppedAt = formatTimeNyangStoppedAt();
  const plainText = `${roundToken}||${clientElapsedSec}||${stoppedAt}`;
  const signatureHex = await sha256Hex(plainText);

  return {
    roundToken,
    clientElapsedSec,
    stoppedAt,
    signatureHex,
  };
};

function TimeNyangMockAdOverlay({ ad }) {
  if (!ad) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 px-5">
      <div className="flex w-[300px] flex-col items-center rounded-2xl bg-white px-6 py-8 text-center shadow-[0_12px_30px_rgba(0,0,0,0.25)]">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F0ECFF] text-[28px]">🎬</div>
        <p className="m-0 font-['Pretendard'] text-[18px] font-extrabold leading-[1.4] text-gray05">
          {MOCK_AD_LABEL_BY_KIND[ad.kind] ?? '광고 시청 중...'}
        </p>
        <p className="m-0 mt-2 font-['Pretendard'] text-[14px] font-bold leading-[1.4] text-gray03">
          API 연결 전 목업 광고입니다
        </p>
        <p className="m-0 mt-5 font-['Pretendard'] text-[36px] font-black leading-none text-primary-purple-01">
          {ad.secondsLeft}
        </p>
      </div>
    </div>
  );
}

export default function TimeNyangPage() {
  const navigate = useNavigate();
  const [isGuideSheetOpen, setIsGuideSheetOpen] = useState(false);
  const [nPayPopupStep, setNPayPopupStep] = useState(null);
  const [isClockGifLoaded, setIsClockGifLoaded] = useState(false);
  const [isPendingTimeNyangAd, setIsPendingTimeNyangAd] = useState(false);
  const [timeNyangAdLoadingKind, setTimeNyangAdLoadingKind] = useState(null);
  const [freePlayAdCompleteTick, setFreePlayAdCompleteTick] = useState(0);
  const [isTimeNyangMockModeEnabled, setIsTimeNyangMockModeEnabled] = useState(() => {
    if (!SHOW_TIME_NYANG_MODE_DEBUG_UI) return DEFAULT_TIME_NYANG_MOCK_MODE;
    try {
      const savedMode = window.localStorage.getItem(TIME_NYANG_API_MODE_STORAGE_KEY);
      if (savedMode === 'mock') return true;
      if (savedMode === 'api') return false;
    } catch (error) {
      // no-op
    }
    return DEFAULT_TIME_NYANG_MOCK_MODE;
  });
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const browserDeviceType = usePlatform();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;
  const roundTokenRef = useRef(null);
  const pendingAdPlacementRef = useRef(null);
  const pendingAdTokenRef = useRef(null);
  const pendingAdCompleteRef = useRef(null);
  const timeNyangAdRunnerRef = useRef(null);
  const handledFreePlayAdCompleteTickRef = useRef(0);
  const shouldResetStoppedOnFreePlayReadyRef = useRef(false);

  useEffect(() => {
    if (typeof window.notifyAds === 'function') return;
    window.notifyAds = (event, reward) => {
      window.dispatchEvent(
        new CustomEvent('ads', {
          detail: { event, reward },
        }),
      );
    };
  }, []);

  const { data: profileInfo, mutate: mutateProfileInfo } = useSWR(
    accessToken && [`${API_SERVER_DOMAIN}/user/profile/info`, accessToken],
    fetchWithToken,
  );
  const { data: timeNyangState, mutate: mutateTimeNyangState } = useSWR(
    accessToken && !isTimeNyangMockModeEnabled && [`${API_SERVER_DOMAIN}${getStateUrl()}`, accessToken],
    fetchWithToken,
    { dedupingInterval: 0, revalidateOnMount: true },
  );
  const currentPoint = profileInfo?.info?.point ?? 0;
  const runTimeNyangRewardAd = useCallback((kind, onComplete) => {
    if (typeof timeNyangAdRunnerRef.current !== 'function') return false;
    return timeNyangAdRunnerRef.current(kind, onComplete);
  }, []);

  const finalRewards = useTimeNyangRewards({
    currentPoint,
    initState: isTimeNyangMockModeEnabled ? null : timeNyangState,
    isMockMode: isTimeNyangMockModeEnabled,
    runRewardAd: runTimeNyangRewardAd,
  });
  const sendStartAdToBackend = useCallback(
    async (placement) => {
      if (!accessToken || !placement) return false;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getTimeNyangStartAdUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ placement, deviceType }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.result === 'fail') {
          finalRewards.showBottomToast(payload?.message || '광고를 불러오지 못했어요.', 'timeout');
          return false;
        }
        return {
          adToken: payload?.adToken ?? null,
          placementId: payload?.placementId ?? '',
          adType: payload?.adType ?? '',
        };
      } catch (error) {
        console.error('backend api: error POST /time-nyang/ads/start', { error });
        finalRewards.showBottomToast('광고를 불러오지 못했어요.', 'timeout');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, deviceType, finalRewards],
  );
  const sendCompleteAdToBackend = useCallback(
    async (placement, adToken) => {
      if (!accessToken || !placement) return false;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getTimeNyangCompleteAdUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            placement,
            ...(adToken ? { adToken } : {}),
          }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.result === 'fail') {
          finalRewards.showBottomToast(payload?.message || '광고 보상 처리에 실패했어요.', 'timeout');
          return false;
        }
        return payload;
      } catch (error) {
        console.error('backend api: error POST /time-nyang/ads/complete', { error });
        finalRewards.showBottomToast('광고 보상 처리에 실패했어요.', 'timeout');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, finalRewards],
  );
  const sendClaimNpayToBackend = useCallback(
    async (adToken) => {
      if (!accessToken) return false;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getClaimNpayUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ adToken }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.result === 'fail') {
          finalRewards.showBottomToast(payload?.message || 'N Pay 교환 처리에 실패했어요.', 'timeout');
          return false;
        }
        finalRewards.handleNPayExchangeConfirm(payload);
        mutateTimeNyangState();
        mutateProfileInfo();
        return payload;
      } catch (error) {
        console.error('backend api: error POST /time-nyang/npay/claim', { error });
        finalRewards.showBottomToast('N Pay 교환 처리에 실패했어요.', 'timeout');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, finalRewards, mutateProfileInfo, mutateTimeNyangState],
  );
  const sendForfeitRewardToBackend = useCallback(
    async (target) => {
      if (!accessToken || !target) return false;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getForfeitRewardUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ target }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.result === 'fail') {
          finalRewards.showBottomToast(payload?.message || '보상 포기 처리에 실패했어요.', 'timeout');
          return false;
        }
        finalRewards.handlePlayStopResponse(payload);
        mutateTimeNyangState();
        mutateProfileInfo();
        return payload;
      } catch (error) {
        console.error('backend api: error POST /time-nyang/rewards/forfeit', { error });
        finalRewards.showBottomToast('보상 포기 처리에 실패했어요.', 'timeout');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, finalRewards, mutateProfileInfo, mutateTimeNyangState],
  );
  const clearTimeNyangAdLoading = useCallback(() => {
    setTimeNyangAdLoadingKind(null);
  }, []);

  const handleTimeNyangAdsError = useCallback(
    (errorInfo = {}) => {
      pendingAdPlacementRef.current = null;
      pendingAdTokenRef.current = null;
      pendingAdCompleteRef.current = null;
      setIsPendingTimeNyangAd(false);
      clearTimeNyangAdLoading();
      const message =
        typeof errorInfo?.message === 'string' && errorInfo.message.trim().length > 0
          ? errorInfo.message
          : '광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.';
      finalRewards.showBottomToast(message, 'timeout');
      mutateTimeNyangState();
    },
    [clearTimeNyangAdLoading, finalRewards, mutateTimeNyangState],
  );
  const startTimeNyangServerPlacementAd = useCallback(
    async (kind, onComplete) => {
      const placement = TIME_NYANG_AD_PLACEMENT_BY_KIND[kind];
      if (!placement) return false;
      if (timeNyangAdLoadingKind) return false;
      if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
        finalRewards.showBottomToast('앱 광고 실행 핸들러를 찾지 못했어요.', 'timeout');
        return false;
      }

      setTimeNyangAdLoadingKind(kind);
      try {
        const adStart = await sendStartAdToBackend(placement);
        if (adStart === false) {
          clearTimeNyangAdLoading();
          return false;
        }

        pendingAdPlacementRef.current = placement;
        pendingAdTokenRef.current = adStart?.adToken ?? null;
        pendingAdCompleteRef.current = onComplete;
        setIsPendingTimeNyangAd(true);
        runAdmob(adStart?.adType ?? '', adStart?.placementId ?? '');
        return true;
      } catch (error) {
        clearTimeNyangAdLoading();
        throw error;
      }
    },
    [clearTimeNyangAdLoading, finalRewards, sendStartAdToBackend, timeNyangAdLoadingKind],
  );
  useEffect(() => {
    timeNyangAdRunnerRef.current = startTimeNyangServerPlacementAd;
    return () => {
      if (timeNyangAdRunnerRef.current === startTimeNyangServerPlacementAd) {
        timeNyangAdRunnerRef.current = null;
      }
    };
  }, [startTimeNyangServerPlacementAd]);

  useAdsListener(
    '',
    '',
    { task: 'time-nyang-ad', pending: isPendingTimeNyangAd, silentErrorAlert: true, onError: handleTimeNyangAdsError },
    async () => {
      if (!isPendingTimeNyangAd) return;
      const placement = pendingAdPlacementRef.current;
      const adToken = pendingAdTokenRef.current;
      const onComplete = pendingAdCompleteRef.current;
      pendingAdPlacementRef.current = null;
      pendingAdTokenRef.current = null;
      pendingAdCompleteRef.current = null;
      setIsPendingTimeNyangAd(false);
      clearTimeNyangAdLoading();

      if (placement === 'npayExchange') {
        await onComplete?.({ placement, adToken });
        return;
      }

      const completePayload = await sendCompleteAdToBackend(placement, adToken);
      if (!completePayload) {
        mutateTimeNyangState();
        return;
      }
      onComplete?.({ placement, adToken, completePayload });
      if (placement === 'freePlay') {
        setFreePlayAdCompleteTick((prev) => prev + 1);
      }
      finalRewards.handlePlayStopResponse(completePayload);
      mutateTimeNyangState();
      mutateProfileInfo();
    },
  );
  const sendPlayStartToBackend = useCallback(
    async (mode) => {
      if (!accessToken) {
        finalRewards.showBottomToast('로그인 후 이용해 주세요.', 'timeout');
        return false;
      }

      try {
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayStartUrl()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            mode: mode === 'free' ? 'free' : 'normal',
          }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.roundToken) {
          finalRewards.handlePlayStartFailure(payload, mode);
          mutateTimeNyangState();
          finalRewards.showBottomToast(payload?.message || '타임냥 시작에 실패했어요.', 'timeout');
          return false;
        }
        roundTokenRef.current = payload.roundToken;
        finalRewards.handlePlayStartResponse(payload, mode);
        return true;
      } catch (error) {
        console.error('backend api: error POST /time-nyang/play/start', { error });
        finalRewards.showBottomToast('타임냥 시작에 실패했어요.', 'timeout');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, finalRewards, mutateTimeNyangState],
  );
  const sendPlayStopToBackend = useCallback(
    async (elapsedMs) => {
      if (!accessToken) {
        finalRewards.showBottomToast('로그인 후 이용해 주세요.', 'timeout');
        return false;
      }
      if (!roundTokenRef.current) {
        finalRewards.showBottomToast('플레이 정보를 찾지 못했어요. 다시 시작해 주세요.', 'timeout');
        return false;
      }

      try {
        const stopPayload = await createTimeNyangStopPayload({
          roundToken: roundTokenRef.current,
          elapsedMs,
        });
        const response = await fetch(`${API_SERVER_DOMAIN}${getPlayStop2Url()}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(stopPayload),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || payload?.result === 'fail') {
          finalRewards.showBottomToast(payload?.message || '타임냥 판정에 실패했어요.', 'timeout');
          return false;
        }
        roundTokenRef.current = null;
        finalRewards.handlePlayStopResponse(payload);
        mutateTimeNyangState();
        mutateProfileInfo();
        return payload;
      } catch (error) {
        console.error('backend api: error POST /time-nyang/play/stop2', { error });
        finalRewards.showBottomToast(formatGameCryptoError(error, '타임냥 판정에 실패했어요.'), 'timeout');
        return false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, finalRewards, mutateProfileInfo, mutateTimeNyangState],
  );
  const finalGame = useTimeNyangGame({
    getStartContext: finalRewards.getStartContext,
    onPlayStart: isTimeNyangMockModeEnabled ? finalRewards.handlePlayStart : sendPlayStartToBackend,
    onPlayStop: isTimeNyangMockModeEnabled ? null : sendPlayStopToBackend,
    onResult: finalRewards.handleRoundResult,
    isBlockedByPopup: finalRewards.isBlockedByPopup,
    initState: isTimeNyangMockModeEnabled ? null : timeNyangState,
  });

  useEffect(() => {
    if (freePlayAdCompleteTick <= 0) return;
    if (handledFreePlayAdCompleteTickRef.current >= freePlayAdCompleteTick) return;
    handledFreePlayAdCompleteTickRef.current = freePlayAdCompleteTick;
    if (finalGame.playState !== 'stopped') return;
    finalGame.resetToIdle();
  }, [freePlayAdCompleteTick, finalGame.playState, finalGame.resetToIdle]);

  useEffect(() => {
    if (!shouldResetStoppedOnFreePlayReadyRef.current) return;
    if (!finalRewards.isFreePlayReady) return;
    if (finalGame.playState !== 'stopped') return;
    shouldResetStoppedOnFreePlayReadyRef.current = false;
    finalGame.resetToIdle();
  }, [finalRewards.isFreePlayReady, finalGame.playState, finalGame.resetToIdle]);

  const handleFreePlayChipClick = useCallback(() => {
    if (
      isTimeNyangMockModeEnabled &&
      finalGame.playState === 'stopped' &&
      !finalRewards.isFreePlayDisabled &&
      !timeNyangAdLoadingKind
    ) {
      shouldResetStoppedOnFreePlayReadyRef.current = true;
    }
    finalRewards.handleFreePlayChipClick();
  }, [finalGame.playState, finalRewards, isTimeNyangMockModeEnabled, timeNyangAdLoadingKind]);

  const freePlayCountText = finalGame.isDailyLocked
    ? '-'
    : `${finalRewards.freePlayRemaining}/${finalRewards.freePlayDailyMax}`;
  const boosterCountText = finalGame.isDailyLocked ? '-' : `${finalRewards.boosterRemaining}/6`;
  const startButtonLabel =
    finalGame.playState === 'idle' && !finalGame.isDailyLocked
      ? finalRewards.idleStartButtonLabel
      : finalGame.startButtonLabel;
  const isStartButtonDisabled =
    finalGame.playState === 'idle'
      ? !isClockGifLoaded || finalGame.isStartPending || finalGame.isDailyLocked || finalRewards.isIdleStartDisabled
      : finalGame.playState === 'running'
        ? finalGame.isStopPending
        : false;
  const visiblePopup =
    finalRewards.rewardPopup ??
    finalRewards.adRestPopup ??
    (nPayPopupStep === 'npay-main'
      ? { kind: 'npay' }
      : nPayPopupStep === 'npay-complete'
        ? { kind: 'npay-complete' }
        : null);
  const visiblePopupAdKind =
    nPayPopupStep === 'npay-main'
      ? 'npay-exchange'
      : finalRewards.adRestPopup
        ? 'ad-rest'
        : finalRewards.rewardPopup?.kind === 'success'
          ? 'success-reward'
          : finalRewards.rewardPopup?.kind === 'near-point' || finalRewards.rewardPopup?.kind === 'near-free'
            ? 'near-point-reward'
            : null;
  const isVisiblePopupAdLoading = Boolean(
    visiblePopupAdKind && timeNyangAdLoadingKind && timeNyangAdLoadingKind === visiblePopupAdKind,
  );
  const proximityOverlayClass =
    finalGame.proximityLevel === 'none'
      ? 'time-nyang-proximity-overlay'
      : `time-nyang-proximity-overlay time-nyang-proximity-overlay--${finalGame.proximityLevel}`;
  const proximityShakeClass =
    finalGame.proximityLevel === 'mid' || finalGame.proximityLevel === 'high'
      ? `time-nyang-proximity-shake--${finalGame.proximityLevel}`
      : '';

  const handleNPayButtonClick = () => {
    if (!finalRewards.canExchangeNPay) {
      finalRewards.handleNPayUnavailableClick();
      return;
    }
    setNPayPopupStep('npay-main');
  };

  const handleToggleTimeNyangApiMode = () => {
    if (!SHOW_TIME_NYANG_MODE_DEBUG_UI) return;
    const nextMockMode = !isTimeNyangMockModeEnabled;
    try {
      window.localStorage.setItem(TIME_NYANG_API_MODE_STORAGE_KEY, nextMockMode ? 'mock' : 'api');
    } catch (error) {
      // no-op
    }
    setIsTimeNyangMockModeEnabled(nextMockMode);
    window.location.reload();
  };

  useEffect(() => {
    const image = new Image();
    image.onload = () => setIsClockGifLoaded(true);
    image.src = CLOCK_IMAGE;

    if (image.complete) {
      setIsClockGifLoaded(true);
    }

    return () => {
      image.onload = null;
    };
  }, []);

  return (
    <>
      <style>{`
      @keyframes npay-border-shimmer {
        0% { transform: translateX(-80%); }
        100% { transform: translateX(80%); }
      }
    `}</style>
      <div className="min-h-screen bg-[#F0ECFF]">
        <div className="relative min-h-screen w-full overflow-hidden bg-[#2D2058] pb-[104px]">
          <div className={proximityOverlayClass} aria-hidden="true" />
          {finalRewards.isBoosterActive ? (
            <img
              src={BURNING_BG_IMAGE}
              alt=""
              className="time-nyang-burning-bg pointer-events-none absolute left-1/2 top-5 z-0 h-[660px] w-[150%] max-w-none -translate-x-1/2 object-cover"
              aria-hidden="true"
            />
          ) : null}
          <img
            src={GLOW_ELLIPSE_TOP_IMAGE}
            alt=""
            className="pointer-events-none absolute left-0 top-[500px] z-0 h-[200px] w-full object-cover"
          />
          <div
            className="pointer-events-none absolute bottom-0 left-0 right-0 top-[620px] z-0 bg-[#F0ECFF]"
            style={{
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 100px)',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 100px)',
            }}
          />
          <TimeNyangTopBar onBack={() => navigate(-1)} onOpenGuide={() => setIsGuideSheetOpen(true)} />

          <img
            src={STAR_TRAIL_IMAGE}
            alt=""
            className="pointer-events-none absolute left-1/2 top-[105px] z-0 h-[361px] w-full object-contain max-w-none -translate-x-1/2 -scale-y-100"
          />

          <div className={`relative z-10 px-2 pb-8 pt-[60px] ${proximityShakeClass}`}>
            <TimeNyangHeader
              freePlayCountText={freePlayCountText}
              boosterCountText={boosterCountText}
              onFreePlayClick={handleFreePlayChipClick}
              onBoosterClick={finalRewards.handleBoosterChipClick}
              isFreePlayActive={finalRewards.isFreePlayReady}
              freePlayCooldownLabel={finalRewards.freePlayCooldownLabel}
              isFreePlayDisabled={finalRewards.isFreePlayDisabled || Boolean(timeNyangAdLoadingKind)}
              isFreePlayLoading={timeNyangAdLoadingKind === 'free-play'}
              boosterStatusLabel={finalRewards.boosterStatusLabel}
              isBoosterDisabled={
                finalRewards.isBoosterOnCooldown || finalRewards.isBoosterActive || Boolean(timeNyangAdLoadingKind)
              }
              isBoosterLoading={timeNyangAdLoadingKind === 'booster-start'}
            />

            <TimeNyangGamePanel
              playState={finalGame.playState}
              timerText={finalGame.timerText}
              startButtonBg={finalGame.startButtonBg}
              startButtonLabel={startButtonLabel}
              isStartButtonDisabled={isStartButtonDisabled}
              dailySuccessCount={finalGame.dailySuccessCount}
              onStartButtonClick={finalGame.handleStartButtonClick}
            />
            {SHOW_TIME_NYANG_MODE_DEBUG_UI ? (
              <div className="mt-2 flex justify-center">
                <button
                  type="button"
                  onClick={handleToggleTimeNyangApiMode}
                  className="rounded-[8px] border-0 bg-white/85 px-3 py-1 font-['Pretendard'] text-[11px] font-bold leading-none text-gray04 shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
                >
                  {isTimeNyangMockModeEnabled ? '목업 모드' : '실서버 모드'}
                </button>
              </div>
            ) : null}

            <TimeNyangScoreCard
              timeScore={finalRewards.timeScore}
              canExchangeNPay={finalRewards.canExchangeNPay}
              onNPayButtonClick={handleNPayButtonClick}
            />

            <TimeNyangPlayLogCard
              bestLog={finalGame.bestLog}
              visiblePlayLogs={finalGame.visiblePlayLogs}
              shouldShowPlayLogToggle={finalGame.shouldShowPlayLogToggle}
              isLogsExpanded={finalGame.isLogsExpanded}
              onToggleLogs={() => finalGame.setIsLogsExpanded((prev) => !prev)}
            />
          </div>

          <TimeNyangBottomPoint point={finalRewards.pointBalance} />
        </div>
      </div>
      {finalRewards.boosterStartEffectEvent ? (
        <div
          key={finalRewards.boosterStartEffectEvent.id}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 px-5"
        >
          {finalRewards.boosterStartTextPhase === 0 ? (
            <p className="text-center font-['Pretendard'] text-[34px] font-extrabold leading-tight text-white [text-shadow:0_4px_20px_rgba(0,0,0,0.55)]">
              🐱🔥 적립부스터 ON
            </p>
          ) : (
            <p className="text-center font-['Pretendard'] text-[34px] font-extrabold leading-tight text-[#DCCAFF] [text-shadow:0_4px_20px_rgba(0,0,0,0.55)]">
              포인트 적립 4배!
            </p>
          )}
        </div>
      ) : null}
      {isTimeNyangMockModeEnabled ? <TimeNyangMockAdOverlay ad={finalRewards.mockAd} /> : null}
      <TimeNyangBottomToast events={finalRewards.bottomToastEvents} />
      <TimeNyangRewardPopup
        popup={visiblePopup}
        isPrimaryAdLoading={isVisiblePopupAdLoading}
        onClose={() => {
          if (nPayPopupStep) {
            setNPayPopupStep(null);
            return;
          }
          finalRewards.closeRewardPopup();
        }}
        onConfirm={() => {
          if (isVisiblePopupAdLoading) return;
          if (nPayPopupStep === 'npay-main') {
            if (isTimeNyangMockModeEnabled) {
              finalRewards.handleNPayExchangeConfirm();
              setNPayPopupStep('npay-complete');
              return;
            }
            runTimeNyangRewardAd('npay-exchange', async ({ adToken }) => {
              const claimPayload = await sendClaimNpayToBackend(adToken);
              if (claimPayload) setNPayPopupStep('npay-complete');
            });
            return;
          }
          if (nPayPopupStep === 'npay-complete') {
            setNPayPopupStep(null);
            return;
          }
          if (finalRewards.adRestPopup) {
            finalRewards.handleAdRestConfirm();
            return;
          }
          finalRewards.handleRewardConfirm();
        }}
        onSkip={() => {
          if (isVisiblePopupAdLoading) return;
          if (nPayPopupStep) {
            setNPayPopupStep(null);
            return;
          }
          if (finalRewards.adRestPopup) {
            if (!isTimeNyangMockModeEnabled) {
              sendForfeitRewardToBackend('tenPlay').then((payload) => {
                if (payload) finalRewards.handleAdRestSkip();
              });
              return;
            }
            finalRewards.handleAdRestSkip();
            return;
          }
          if (!isTimeNyangMockModeEnabled && finalRewards.rewardPopup) {
            sendForfeitRewardToBackend('pending').then((payload) => {
              if (payload) finalRewards.handleRewardSkip();
            });
            return;
          }
          finalRewards.handleRewardSkip();
        }}
      />
      <PunchInfoBottomSheet
        isOpen={isGuideSheetOpen}
        onClose={() => setIsGuideSheetOpen(false)}
        title={TIME_NYANG_GUIDE_CONTENT.title}
        highlights={TIME_NYANG_GUIDE_CONTENT.highlights}
        sections={TIME_NYANG_GUIDE_CONTENT.sections}
        notices={TIME_NYANG_GUIDE_CONTENT.notices}
        confirmLabel="확인"
      />
    </>
  );
}
